import { useCallback } from 'react';
import {
  RECOMMANDER_SLUG_TO_BUCKET,
  type Card,
  type RecommanderCard,
  type RecommanderResponse,
  type SearchResponse,
} from '@mtg-deck-tool/shared';
import { useSearchStore } from '../stores/search-store.js';
import { useDeckStore } from '../stores/deck-store.js';
import { api, ApiClientError } from '../lib/api-client.js';
import { buildSearchQuery } from '../lib/query-builder.js';

type RecommanderData = RecommanderResponse['data'];

/**
 * Pull the cached RecommanderCard list for one bucket slug. 'top' is virtual —
 * it maps to the headline `recommendations` array filtered to !generic, the
 * same view recommander.cards calls "Top picks". Other slugs map directly to
 * `typed_recommendations[bucket]`.
 */
function bucketRecs(slug: string, data: RecommanderData): RecommanderCard[] {
  const bucket = RECOMMANDER_SLUG_TO_BUCKET[slug];
  if (!bucket) return [];
  if (bucket === 'Top') return data.recommendations.filter((r) => !r.generic);
  return data.typed_recommendations[bucket] ?? [];
}

/**
 * Strip `rectag:<slug>` tokens from the bar text and resolve them to the cached
 * recommander cards. Each rec carries an optional `resolvedCard` (Card object
 * from our local DB), populated by useRecommendations after the initial fetch.
 * We surface only those resolved cards — unresolvable cards (rare; recent
 * printings missing from the local DB) get dropped silently.
 *
 * Returns the cleaned bar text + the cards to show. We bypass Scryfall entirely
 * when a rectag is present: Scryfall's `q` parameter caps around ~1000 chars,
 * which a 42-card oracleid: OR group blows past, and we already have the cards
 * locally so the round-trip is wasteful anyway.
 */
function expandRecTags(
  userInput: string,
  data: RecommanderData | null,
): { cleanedInput: string; recCards: Card[]; hadRecTag: boolean } {
  const slugs: string[] = [];
  const cleanedInput = userInput
    .replace(/rectag:(\S+)/g, (_, slug: string) => {
      slugs.push(slug);
      return '';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (slugs.length === 0) return { cleanedInput, recCards: [], hadRecTag: false };

  const seen = new Set<string>();
  const recCards: Card[] = [];
  if (data) {
    for (const slug of slugs) {
      for (const rec of bucketRecs(slug, data)) {
        if (!rec.resolvedCard || seen.has(rec.oracle_id)) continue;
        seen.add(rec.oracle_id);
        recCards.push(rec.resolvedCard);
      }
    }
  }
  return { cleanedInput, recCards, hadRecTag: true };
}

export function useSearch() {
  const setResults = useSearchStore((s) => s.setResults);
  const setLoading = useSearchStore((s) => s.setLoading);
  const setError = useSearchStore((s) => s.setError);

  const execute = useCallback(
    async (page = 1) => {
      // Read from store at call time — avoids stale closure issues
      const { userInput, filters, activeTagSlugs, sortOrder, sortDir, advancedFilters } = useSearchStore.getState();
      const { colorIdentity, moxfieldDeckId, recommendationsCache } = useDeckStore.getState();

      // Resolve any rectag clauses against the cache before deciding whether to call Scryfall.
      const recData = moxfieldDeckId
        ? ((recommendationsCache.get(moxfieldDeckId) as RecommanderData | undefined) ?? null)
        : null;
      const { cleanedInput, recCards, hadRecTag } = expandRecTags(userInput, recData);

      // rectag clause present → serve straight from the cache. Other Scryfall
      // tokens in the bar are intentionally ignored; the bucket is the view.
      if (hadRecTag) {
        const response: SearchResponse = {
          cards: recCards,
          totalCards: recCards.length,
          hasMore: false,
        };
        setResults(response, page);
        return;
      }

      const query = buildSearchQuery(cleanedInput, filters, colorIdentity, activeTagSlugs, advancedFilters);

      // Require at least one user-supplied term beyond the context filters
      const hasAdvancedContent =
        advancedFilters.cardName.trim() !== '' ||
        advancedFilters.oracleText.trim() !== '' ||
        advancedFilters.types.length > 0 ||
        advancedFilters.colors.selected.length > 0 ||
        advancedFilters.manaCost.trim() !== '' ||
        advancedFilters.stats.power !== null ||
        advancedFilters.stats.toughness !== null ||
        advancedFilters.stats.manaValue !== null ||
        advancedFilters.set.trim() !== '' ||
        advancedFilters.rarity !== '';

      const hasUserContent =
        userInput.trim().length > 0 ||
        activeTagSlugs.length > 0 ||
        hasAdvancedContent ||
        Object.values(filters).some((v) => v !== undefined && v !== '');

      if (!hasUserContent) return;

      setLoading(true);
      try {
        const res = await api.search(query, { page, order: sortOrder, dir: sortDir });
        setResults(res, page);
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Search failed');
      }
    },
    [setResults, setLoading, setError],
  );

  return { execute };
}
